import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import { isConversationUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';
import {
  getConversationById,
  getUseConversationQKey,
} from '../../../../conversations/src/features/detail/shared/api/getConversationById';
import { ConversationDetail } from '../../../../conversations/src/features/overview/types/DetailTypes';

interface UseFlowsConversation {
  checkConversationCompletion: (sessionId: string) => Promise<void>;
  getPartyFromConversation: (sessionId: string) => Promise<string | undefined>;
  getConversation: (
    sessionId: string,
  ) => Promise<ConversationDetail | undefined>;
}

/**
 * These hook are related to checking the conversation state of the session
 */
const useFlowsConversation = (): UseFlowsConversation => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { search } = useLocation();

  /**
   * This will check if the conversation is already completed and push
   * the user towards that route.
   */
  const checkConversationCompletion = async (sessionId: string) => {
    const lastConversation = await getConversation(sessionId);

    if (lastConversation?.status === 'COMPLETED') {
      if (isConversationUrl) {
        // Allow Yellowhive deeplink resume even if the conversation is completed
        // When users arrive with ?deeplink=slotvragen we should not block them
        const params = new URLSearchParams(search);
        const deeplink = params.get('deeplink');
        if (deeplink === 'slotvragen') return;
        return history.replace({
          pathname: '/company-scan/already-completed',
          search,
        });
      }
    }
  };

  const getPartyFromConversation = async (sessionId: string) => {
    const lastConversation = await getConversation(sessionId);
    return lastConversation?.prospect?.id;
  };

  /**
   * Will receive the conversation from cache and otherwise fetch it
   */
  const getConversation = async (sessionId: string) => {
    let lastConversation;
    const qKey = getUseConversationQKey(sessionId);
    lastConversation = queryClient.getQueryData<ConversationDetail>(qKey);
    if (!lastConversation) {
      try {
        lastConversation = await getConversationById(sessionId);
        queryClient.setQueryData(qKey, lastConversation);
      } catch (err) {
        console.error(err);
      }
    }
    return lastConversation;
  };

  return {
    checkConversationCompletion,
    getPartyFromConversation,
    getConversation,
  };
};

export default useFlowsConversation;
